import {
  Card,
  BlockStack,
  Text,
  FormLayout,
} from "@medentic/polaris-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { Event} from "@features/events/types";
import { BrandCustomSystem } from "@features/events/types/brand";
import { GenerativeTextField } from "@shared/ui";
import { EventSlugField } from "@shared/ui";
import { EntityType } from "@shared/types/commonTypes";
import EventSourceUrlField from "./EventSourceUrlField";

interface OverviewCardProps {
  eventData: Partial<Event>;
  onDataChange: (updates: Partial<Event>) => void;
  fieldErrors: Record<string, string>;
  brandCustomSystem?: BrandCustomSystem | null;
}

const resolveCustomSourceUrl = (
  customSystem: BrandCustomSystem | null | undefined,
  slug: string | undefined
): string | null => {
  const pattern = customSystem?.sourceUrlPattern?.trim();
  if (!pattern) {
    return null;
  }

  return pattern.replace("{slug}", slug ?? "");
};

export default function OverviewCard({
  eventData,
  onDataChange,
  fieldErrors,
  brandCustomSystem,
}: OverviewCardProps) {
  const { t } = useTranslation();
  const { eventOrganizerId: hcc } = useParams<{
    eventOrganizerId: string;
  }>();
  const customSourceUrlPattern = brandCustomSystem?.sourceUrlPattern?.trim();
  const customSourceUrl = resolveCustomSourceUrl(brandCustomSystem, eventData.slug);
  const isCustomSourceMode = Boolean(customSourceUrlPattern);

  return (
    <Card>
      <BlockStack gap="400">
        <Text as="h2" variant="headingMd">
          {t("eventDetails.overview.title")}
        </Text>

        <FormLayout>
          <GenerativeTextField
            id="title-bg"
            placementId="event.event.title"
            label={`${t("eventDetails.title")}`}
            value={eventData.title ?? {}}
            onChange={(value) =>
              onDataChange({
                title: value,
              })
            }
            type="textField"
            canGenerate={true}
            requiredIndicator
            error={fieldErrors.title}
          />

          <EventSlugField
            value={eventData.slug || ""}
            onChange={(value) => onDataChange({ slug: value })}
            healthcareCompanyId={hcc || null}
            entityId={eventData.id}
            entityType={EntityType.EVENT}
            error={fieldErrors.slug}
            requiredIndicator
          />

          <GenerativeTextField
            id="description-en"
            placementId="event.event.description"
            label={`${t("eventDetails.overview.description")}`}
            value={eventData.description ?? {}}
            onChange={(value) =>
              onDataChange({
                description: value,
              })
            }
            type="textArea"
            canGenerate={true}
            requiredIndicator
            error={fieldErrors.description}
          />

          <EventSourceUrlField
            sourceUrl={isCustomSourceMode ? customSourceUrl : eventData.sourceUrl}
            onDataChange={(value) => {
              if (!isCustomSourceMode) {
                onDataChange({ sourceUrl: value });
              }
            }}
            error={fieldErrors.sourceUrl}
            disabled={isCustomSourceMode}
            helpText={
              isCustomSourceMode
                ? t("eventDetails.overview.sourceUrlHelpCustom")
                : undefined
            }
          />
        </FormLayout>
      </BlockStack>
    </Card>
  );
}
